import React, { useEffect, useRef, useState } from "react";
import {isEmpty, sumBy, includes, forEach} from "lodash";
import moment from 'moment';
import DateUtil from "../utils/date-util.service";
import { GetData } from "../Api";
import CustomBarChart from "../Components/BarChart/custom-bar-chart.component.js";
import CustomRadialChart from "../Components/RadialChart/custom-radial-chart.component.js";
import WidgetLoader from "../Components/loaders/widget-loader.component.js";
import PnPUtilService from "../utils/pnp-util.service.js";
import { useTranslation } from "react-i18next";
moment.suppressDeprecationWarnings = true;

const PnpProductionCount = (props) => {
  const { t } = useTranslation();
  let dataRefreshTimer = useRef(null);
  let radialBarLabels = ['Target', 'Actual'];
  let shift = props.shift;
  let barChartYAxisData = useRef([]);
  let barChartXAxisLabels = useRef([]);
  let lane1RadialData = useRef([]);
  let lane2RadialData = useRef([]);
  let [loading, setLoading] = useState(true);

  const refreshData = (start, end) => {
    destroyDataRefresh();
    let startTs = moment(start ?? props.from).unix();
    let endTs = moment(end ?? props.to).unix();
    getProductionCountData(startTs, endTs);
    dataRefreshTimer.current = setTimeout(()=>{
      let diff = 0;
      if(props.rangeSelected){
        diff = 3*60*1000; // XXX -> 3 mins x 60 seconds x 1000 milliseconds
      }
      refreshData((props.from+diff), (props.to+diff));
    }, PnPUtilService.PNP_MACHINE_STATUS_REFRESH);
  };

  const destroyDataRefresh = ()=>{
    if(dataRefreshTimer.current){
      clearTimeout(dataRefreshTimer.current);
    }
  };

  const resetGraph = () => {
    barChartYAxisData.current = [];
    barChartXAxisLabels.current = [];
    lane1RadialData.current = [];
    lane2RadialData.current = [];
  };

  const getProductionCountData = (startTs, endTs) => {
    setLoading(true);
    resetGraph();
    GetData( `dashboard/pick-place?PNP=PRODUCTION_COUNT&factoryId=${props.factory.factoryId}&startTs=${startTs}&endTs=${endTs}&machineId=${props.selectedMachineName}&reportType=production_count&shift=${shift.id}` )
    .then((response) => {
      drawProductionCountChart(response?.data ?? []);
    })
    .catch(() => {
      drawProductionCountChart([]);
    });
  };

  const drawProductionCountChart = (data) => {
    if(!isEmpty(data)){
      let lane1Data, lane2Data, xCategories = [];
      for (let i = 0; i < shift.duration; i++) {
        xCategories.push(i)
      }
      barChartXAxisLabels.current = xCategories;
        if (Object.keys(data).length !== 0) {
          const shiftHours = shift.id === 'All' ? 
          [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23] 
          : (shift.id === 'a'? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11] : [12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23]);
          if (!isEmpty(data.LANE1)) {
            lane1Data = generateLaneDetails('Lane 1', data.LANE1, shift.duration, shiftHours);
          } else {
            lane1Data = {
                name: 'Lane 1',
                data: []
              }
          }
          if (!isEmpty(data.LANE2)) {
            lane2Data = generateLaneDetails('Lane 2', data.LANE2, shift.duration, shiftHours);
          } else {
            lane2Data = {
                name: 'Lane 2',
                data: []
              }
          }
          
        } else {
          lane1Data = {
              name: 'Lane 1',
              data: []
            };
            lane2Data = {
              name: 'Lane 2',
              data: []
            };
        }
        let laneData = [];
        laneData.push(lane1Data);
        laneData.push(lane2Data);
        barChartYAxisData.current = laneData;
    }else{
      resetGraph();
    }
    setLoading(false);
  }
  
  const generateLaneDetails = (name, data, shiftTime, shiftHours) => {
    let hourDataCollection = {};
    if (!data) {
      if(name === 'Lane 1'){
        lane1RadialData.current = [];
      }else{
        lane2RadialData.current = [];
      }
      return false;
    }
    const mappedHours = DateUtil.getFactoryHourMap();
    data.forEach((laneItems) => {
      const hour = DateUtil.getFactoryNthHour(mappedHours, laneItems.ts, props.factory?.timezone);
      if(includes(shiftHours, parseInt(hour, 10))){
        if (!hourDataCollection[hour]) {
          hourDataCollection[hour] = []
        }
        hourDataCollection[hour].push(laneItems);
      }
    });
    let xCategories = [];
    const updatedDataCollection = {};
    forEach( shiftHours, (hourNumber, index)=>{
      xCategories.push(index);
      if(!isEmpty(hourDataCollection[hourNumber])){
        updatedDataCollection[index] = hourDataCollection[hourNumber]
      }else{
        updatedDataCollection[index] = [];
      }
    });

    let boardDataCollection = {}, actualCount = 0;
    for (const key in updatedDataCollection) {
      let boardCount = sumBy(updatedDataCollection[key], (o)=>{ return parseInt(o.boardcount, 10)});
      boardDataCollection[key] = boardCount;
      actualCount += boardCount;
    }
    let targetCount = shift.target_Count * (shift.duration - shift.break);
    let performancePercentage = (actualCount * 100) / targetCount;

    let hourlyChartCollection = []
    for (let j = 0; j < shiftTime; j++) {
      hourlyChartCollection[j] = boardDataCollection[j];
    }

    let hourlyData ={
        name: name,
        data: hourlyChartCollection
      };

      if(name === 'Lane 1'){
        lane1RadialData.current = [targetCount,actualCount,performancePercentage.toFixed(2)];
      }else{
        lane2RadialData.current = [targetCount,actualCount,performancePercentage.toFixed(2)];
      }

    return hourlyData;

  }
  
  useEffect(()=>{
    if(!isEmpty(props.factory) 
    && !isEmpty(props.selectedMachineName) 
    && !isEmpty(props.shift) 
    && !isNaN(props.from) 
    && !isNaN(props.to)){
      refreshData();
    }
    return ()=>{
      destroyDataRefresh();
    };
  }, [props]);

  return (
    <div className="cv-pnp-production-count">
      <div className="px-4 pt-2">
        <div className="d-flex justify-content-between">
          <div className="inspection-dashboard-title">
            {t("LT_hourly_production_count_chart")}
          </div>
      </div>
      <div className="d-flex justify-content-between">
        <div className="cv-pnp-count-charts w-100">
        {loading ? 
          <div className="cv-count-loader d-flex justify-content-center align-items-center">
          <WidgetLoader /> 
          </div>  : 
          <CustomBarChart series={barChartYAxisData.current} labels={barChartXAxisLabels.current} 
            title = {t('LT_count')} isStacked={true} labelSuffix="" width={"100%"} height={250} labelDisabled={true} 
            xAxisTitle = {t('LT_time_hours')} yAxisTitle={t('LT_number_of_counts')} legendPosition = "top" showTooltipTitle={true}
            fillColors={["#26a0fc","#26e7a6"]}/>
        }
        </div>
        <div className="d-flex flex-column cv-production-metrics">
          <div className="cv-radial-chart">
            <CustomRadialChart series={[lane1RadialData.current[0],lane1RadialData.current[1]]} 
              labels ={radialBarLabels} title={t("LT_lane_1_perf")} 
              innerText={lane1RadialData.current[2]??0} colors={["#888","#26a0fc"]}/>
          </div>
          <div className="cv-radial-chart">
            <CustomRadialChart series={[lane2RadialData.current[0],lane2RadialData.current[1]]} 
              labels ={radialBarLabels} title={t("LT_lane_2_perf")} 
              innerText={lane2RadialData.current[2]??0} colors={["#888","#26e7a6"]}/>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};
export default PnpProductionCount;